import React, { useState, useEffect, useContext } from 'react';
import {
  Box, Typography, Tabs, Tab, Paper, Button, TextField, FormControl, Select, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody, Checkbox, IconButton, Alert,
  CircularProgress, Snackbar, Switch, FormControlLabel, Tooltip,
  useTheme
} from '@mui/material';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import JSONPretty from 'react-json-pretty';
import { Send as SendIcon, Add as AddIcon, Delete as DeleteIcon, Lock as LockIcon, LockOpen as LockOpenIcon, Info as InfoIcon } from '@mui/icons-material';
import { CryptoTool, encryptData, decryptData } from '../utils/CryptoTool';

import 'react-json-pretty/themes/monikai.css';
import { AuthContext } from '../utils/AuthContext';

const SECRET_KEY = `A1b2C3d4E5f6G7h8I9j0K1l2M3n4O512`.padEnd(32, ' ');

const PostmanRealAPi = ({ endpoint }) => {
  // --- State

  const theme = useTheme();
  const [method, setMethod] = useState(endpoint.methods?.[0] || 'GET');
  const [headers, setHeaders] = useState(
    [...endpoint.headers, { key: 'Content-Type', value: 'application/json', enabled: true }]
  );
  const [body, setBody] = useState(
    typeof endpoint.requestBody === 'string'
      ? endpoint.requestBody
      : JSON.stringify(endpoint.requestBody, null, 2)
  );
  const [path, setPath] = useState();
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('documentation');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [encryptEnabled, setEncryptEnabled] = useState(false);
  const [decryptEnabled, setDecryptEnabled] = useState(false);
  const [error, setError] = useState(null);

  // --- RESET ALL STATE WHEN endpoint/myOrgName CHANGES ---
  useEffect(() => {
    console.log("--====");
    setMethod(endpoint.methods?.[0] || 'GET');
    setHeaders(endpoint.headers || [{ key: 'Content-Type', value: 'application/json', enabled: true }]);
    setBody(
      typeof endpoint.requestBody === 'string'
        ? endpoint.requestBody
        : JSON.stringify(endpoint.requestBody, null, 2)
    );
    setPath(endpoint.path)
    setResponse(null);
    setLoading(false);
    setEncryptEnabled(false);
    setDecryptEnabled(false);
    setError(null);
    setActiveTab('documentation');
  }, [endpoint]);

  // Generate cURL command
  const generateCurl = () => {
    let curl = `curl -X ${method} "${endpoint.path}" \\\n`;
    headers.filter(h => h.enabled).forEach(h => {
      curl += `  -H "${h.key}: ${h.value}" \\\n`;
    });
    if (['POST', 'PUT', 'PATCH'].includes(method) && body) {
      curl += `  -d '${body.replace(/\n/g, '\\n')}'`;
    }
    return curl;
  };

  // Handle API request
  const handleSend = async () => {
    setLoading(true); setError(null); setResponse(null);
    try {
      const hdr = {};
      headers.forEach(h => { if (h.enabled) hdr[h.key] = h.value; });

      let requestBody = body;
      // if (encryptEnabled && ["POST", "PUT"].includes(method) && body) {
      console.log("_____________________________");

      requestBody = await encryptData(body, SECRET_KEY);
      // hdr["Content-Encrypted"] = "true";
      // }

      const res = await fetch(path, {
        method,
        headers: hdr,
        ...(method !== "GET" && { body: requestBody })
      });
      let text = await res.text();
      let show = text;

      // Decrypt if needed
      if (decryptEnabled && res.headers.get("Content-Encrypted") === "true") {
        try {
          show = await decryptData(text, SECRET_KEY);
        } catch (e) {
          setError("Decryption failed. Showing raw response.");
        }
      }

      try { show = JSON.stringify(JSON.parse(show), null, 2); } catch { }

      setResponse({ status: res.status, data: show, source: text, encrypted: res.headers.get("Content-Encrypted") === "true" });
    } catch (e) {
      setError("Request failed: " + (e.message || e));
    } finally { setLoading(false); }
  };

  // header helpers
  const updateHeader = (idx, field, value) => {
    const newHeaders = [...headers];
    newHeaders[idx][field] = value;
    setHeaders(newHeaders);
  };
  const toggleHeader = idx => {
    const newHeaders = [...headers];
    newHeaders[idx].enabled = !newHeaders[idx].enabled;
    setHeaders(newHeaders);
  };
  const addHeader = () => setHeaders([...headers, { key: '', value: '', enabled: true }]);
  const removeHeader = idx => {
    const newHeaders = [...headers];
    newHeaders.splice(idx, 1);
    setHeaders(newHeaders);
  };

  const getMethodColor = method => (
    method === 'GET' ? '#61affe' :
      method === 'POST' ? '#49cc90' :
        method === 'PUT' ? '#fca130' :
          method === 'DELETE' ? '#f93e3e' :
            '#50e3c2'
  );
  const getStatusColor = status => (
    status >= 200 && status < 300 ? 'success' :
      status >= 400 && status < 500 ? 'warning' : 'error'
  );
  const handleCopy = () => {
    setSnackbarOpen(true);
    setTimeout(() => setSnackbarOpen(false), 2000);
  };

  return (
    <Box sx={{
      // maxWidth: '35vw',
      // borderRadius: 3,
      bgcolor: 'background.paper',
      // margin: '0',
       p: 1
    }}>
      {/* Header */}
      <Box sx={{ mb: 1 }}>
        <Typography gutterBottom>{endpoint.name}</Typography>
        <Typography color="text.secondary">{endpoint.description}</Typography>
      </Box>

      {/* Tabs */}

      <Tabs
        value={activeTab}
        onChange={(e, v) => setActiveTab(v)}
        textColor="inherit"
        indicatorColor="primary"
        sx={{
          minHeight: 40,
        
          '& .MuiTab-root': {
            minHeight: 40,
            px: 3,  m:1,
            fontWeight: 600,
            fontSize: 14,
            textTransform: 'none',
            // color: '#FFFFFFBB',
            transition: 'color 0.3s ease',
          },
          '& .Mui-selected': {
            color: 'black',
            background: 'linear-gradient(45deg, #F7F1F2FF, #D8BCF7FF)',
            borderRadius: 2,
            boxShadow: '0 2px 8px #ff416cAA',
          },
          // Smooth indicator transition
          '& .MuiTabs-indicator': {
            height: 0,
          },
        }}
      >
        <Tab label="Documentation" value="documentation" />
        <Tab label="Test API" value="testing" />
      </Tabs>

  

      {activeTab === 'documentation' ? (
        <Box sx={ {background: 'linear-gradient(45deg, #F7F1F2FF, #D8BCF7FF)',p:'1px'}}>
          {/* Endpoint Details */}
          <Paper
            elevation={3}
            sx={{
              p: 3,
              maxWidth: '48vw',
              // bgcolor: '#1e1e2f',
              color: '#f0f0f5',
              boxShadow: '0 0 15px rgba(100,100,150,0.3)',
              fontFamily: '"Segoe UI Mono", monospace',
            }}
          >
            <Box sx={{
              display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap',
              bgcolor: '#121224', p: 1,
              justifyContent: 'space-between'
            }}>
              <Box
                sx={{
                  px: 2,
                  py: 0.7,
                  borderRadius: 1.5,
                  fontWeight: 'bold',
                  fontSize: 13,
                  mr: 3,

                  textTransform: 'uppercase',
                  bgcolor: getMethodColor(method),
                  color: '#fff',
                  minWidth: 56,
                  textAlign: 'center',
                  boxShadow: `0 0 8px ${getMethodColor(method)}66`,
                  userSelect: 'none',
                  fontFamily: 'inherit',
                }}
              >
                {method}
              </Box>

              <CopyToClipboard text={generateCurl()} onCopy={handleCopy}>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{
                    ml: 1,
                    borderColor: '#6c6cff',
                    color: '#6c6cff',
                    textTransform: 'uppercase',
                    fontWeight: '600',
                    '&:hover': {
                      bgcolor: '#6c6cff22',
                      borderColor: '#6c6cff',
                      color: '#4a4aff',
                    },
                  }}
                >
                  Copy cURL
                </Button>
              </CopyToClipboard>
              {/* <Typography
        variant="body1"
        sx={{
          flex: 1,
          fontFamily: 'monospace',
          fontSize: 15,
          overflowX: 'auto',
          color: '#c0c0f0',
          
          userSelect: 'text',
          pr: 1,
          whiteSpace: 'nowrap',
        }}
        title={path}
      >
        {path}
      </Typography> */}
              <TextField
                value={path}
                fullWidth
                variant="outlined"
                sx={{
                  bgcolor: '#22233a',
                  input: {
                    color: '#ececff',
                    fontFamily: 'monospace',
                    fontSize: 15,
                  },
                  '& .MuiOutlinedInput-root': {
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    borderLeft: 'none',
                    '& fieldset': {
                      borderColor: '#5a5b7a',
                    },
                    '&:hover fieldset': {
                      borderColor: '#7c4dff',
                    },
                  },
                }}
                onChange={e => setPath(e.target.value)}
              />


            </Box>

            {/* Parameters */}
            {endpoint.parameters && endpoint.parameters.length > 0 && (
              <Box >
                <Typography
                  gutterBottom
                  sx={{ color: '#a0a0d8', fontWeight: '600', letterSpacing: 0.5 }}
                >
                  Parameters
                </Typography>
                <Table
                  sx={{
                    border: '1px solid #444466',
                    borderRadius: 2,
                    '& .MuiTableCell-root': {
                      borderColor: '#65658AFF',
                      fontSize: 14,
                      // color: '#d0d0ff',
                      paddingY: 1,
                    },
                    '& .MuiTableHead-root': {
                      bgcolor: '#696982FF',
                    },
                    '& .MuiTableRow-root:hover': {
                      bgcolor: '#333355',
                    },
                  }}
                  size="small"
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 160, fontWeight: 'bold' }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                      <TableCell sx={{ width: 110, fontWeight: 'bold' }}>Type</TableCell>
                      <TableCell sx={{ width: 90, fontWeight: 'bold' }}>Required</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {endpoint.parameters.map((param, i) => (
                      <TableRow key={i} tabIndex={0} sx={{ '&:focus-visible': { outline: '2px solid #6c6cff' } }}>
                        <TableCell sx={{ fontFamily: 'monospace', color: '#a0a0ff' }}>
                          {param.name}
                        </TableCell>
                        <TableCell>{param.description}</TableCell>
                        <TableCell sx={{ textAlign: 'center', fontWeight: '600' }}>{param.type}</TableCell>
                        <TableCell sx={{ textAlign: 'center', fontWeight: '600' }}>
                          {param.required ? 'Yes' : 'No'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            {/* Request Body */}
            {endpoint.requestBody && (
              <Box >
                <Typography
                  gutterBottom
                  sx={{ color: '#a0a0d8', fontWeight: '600', letterSpacing: 0.5 }}
                >
                  Request Body
                </Typography>
                <Box
                  sx={{
                    borderRadius: 2,
                    border: '1px solid #444466',
                    overflow: 'auto',
                    maxHeight: 240,
                    bgcolor: '#191a2b',
                    fontSize: 14,
                    fontFamily: '"Fira Mono", monospace',
                    color: '#c6caff',
                    px: 2,
                    py: 1.5,
                    userSelect: 'text',
                  }}
                >
                  <JSONPretty id="json-pretty" data={endpoint.requestBody}
                   theme="monikai" />
                </Box>
              </Box>
            )}

            {/* Response Examples */}
            {endpoint.responses && (
              <Box>
                <Typography
                  gutterBottom
                  sx={{ color: '#a0a0d8', fontWeight: '600', letterSpacing: 0.5, mb: 2 }}
                >
                  Response Examples
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap',}}>
                  {Object.keys(endpoint.responses).map((type, i) => (
                    <Button
                      key={i}
                      variant="outlined"
                      onClick={() => setResponse(endpoint.responses[type])}
                      sx={{
                        textTransform: 'capitalize',
                        px: 2,
                        py: 1,
                        borderColor: '#6c6cff',
                        color: '#6c6cff',
                        fontWeight: '600',
                        fontSize: 14,
                        '&:hover': {
                          bgcolor: '#6c6cff22',
                          borderColor: '#6c6cff',
                          color: '#4a4aff',
                        },
                      }}
                    >
                      {type} ({endpoint.responses[type]?.status})
                    </Button>
                  ))}
                </Box>
              </Box>
            )}

            {/* Response Display */}
            {response && (
              <Box>
                <Typography
                  gutterBottom
                  sx={{ color: '#a0a0d8', fontWeight: '600', letterSpacing: 0.5 }}
                >
                  Response
                </Typography>
                <Alert severity={getStatusColor(response.status)} sx={{ mb: 2, fontWeight: '600' }}>
                  Status: {response.status}
                </Alert>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    maxHeight: 320,
                    overflow: 'auto',
                    bgcolor: '#141429',
                    borderColor: '#6c6cff88',
                    borderRadius: 2,
                    fontFamily: '"Fira Mono", monospace',
                    fontSize: 14,
                    color: '#c0c0ff',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {typeof response.data === 'object'
                    ? JSON.stringify(response.data, null, 2)
                    : response.data}
                </Paper>
              </Box>
            )}
          </Paper>
        </Box>

      ) : (
        <Box sx={ 
          {
            // background: 'linear-gradient(45deg, #F7F1F2FF, #3F2A55FF)'
          p:'1px'}}>
          {/* Wrapper Paper with animated gradient border when loading */}
          <Paper
            elevation={3}
            sx={{
              p: 3,
              mb: 2,
              position: 'relative',
              overflow: 'hidden',
              // bgcolor: '#191b2a',
              color: '#e0e0f0',
              boxShadow: '0 4px 15px rgba(0,0,0,0.4)',
              transition: 'border 0.4s ease',
              border: loading ? '3px solid transparent' : 'none',
              borderImageSlice: 2,
              borderImageSource: loading
                ? 'linear-gradient(90deg, #00e5ff, #7c4dff, #ff4081)'
                : undefined,
              animation: loading ? 'borderMove 3s linear infinite' : undefined,
              '@keyframes borderMove': {
                '0%': { borderImageSource: 'linear-gradient(90deg, #00e5ff, #7c4dff, #ff4081) ' },
                '50%': { borderImageSource: 'linear-gradient(90deg, #ff4081, #00e5ff, #7c4dff)' },
                '100%': { borderImageSource: 'linear-gradient(90deg, #00e5ff, #7c4dff, #ff4081)' },
              },
            }}
          >
            {/* Method selector, URL input and send button */}
            <Box sx={{ display: 'flex', alignItems: 'center',  gap: 1 }}>
              <FormControl sx={{ minWidth: 110 }}>
                <Select
                  value={method}
                  onChange={e => setMethod(e.target.value)}
                  variant="outlined"
                  sx={{
                    color: '#e0e0f0',
                    bgcolor: '#22233a',
                    borderRadius: 2,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#5a5b7a',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#7c4dff',
                    },
                    '& .MuiSelect-icon': {
                      color: '#9a9abb',
                    },
                  }}
                >
                  {endpoint.methods.map(m => (
                    <MenuItem key={m} value={m}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                value={path}
                fullWidth
                variant="outlined"
                sx={{
                  bgcolor: '#22233a',
                  input: {
                    color: '#ececff',
                    fontFamily: 'monospace',
                    fontSize: 15,
                  },
                  '& .MuiOutlinedInput-root': {
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    borderLeft: 'none',
                    '& fieldset': {
                      borderColor: '#5a5b7a',
                    },
                    '&:hover fieldset': {
                      borderColor: '#7c4dff',
                    },
                  },
                }}
                onChange={e => setPath(e.target.value)}
              />

              <Button
                variant="contained"
                color="primary"
                onClick={handleSend}
                disabled={loading}
                startIcon={<SendIcon />}
                sx={{
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                  px: 3,
                  py: 1.5,
                  fontWeight: '600',
                  letterSpacing: 0.5,
                  boxShadow: loading ? '0 0 12px #7c4dffbb' : 'none',
                  background: loading
                    ? 'linear-gradient(270deg, #00e5ff, #7c4dff, #ff4081)'
                    : undefined,
                  backgroundSize: '600% 600%',
                  animation: loading ? 'pulse 3s ease infinite' : 'none',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    background:
                      loading
                        ? undefined
                        : 'linear-gradient(135deg, #7c4dff, #5541d1)',
                    boxShadow: loading ? undefined : '0 6px 15px #5541d1bb',
                  },
                  '@keyframes pulse': {
                    '0%': { backgroundPosition: '0% 50%' },
                    '50%': { backgroundPosition: '100% 50%' },
                    '100%': { backgroundPosition: '0% 50%' },
                  },
                }}
              >
                {loading ? 'Sending...' : 'Send'}
              </Button>
            </Box>

            {/* Headers Editor */}
            <Box >
              <Typography
                variant="subtitle1"
                sx={{
                  mb: 1,
                  fontWeight: '600',
                  color: '#9b9baf',
                  letterSpacing: 0.5,
                  userSelect: 'none',
                }}
              >
                Headers
              </Typography>

              <Table
                size="small"
                sx={{
                  border: '1px solid #3a3b5a',
                  borderRadius: 3,
                  '& .MuiTableCell-root': {
                    color: '#d0d0f5',
                    borderColor: '#3a3b5a',
                    fontSize: 13,
                    py: 1,
                  },
                  '& .MuiTableHead-root': {
                    bgcolor: '#2b2c44',
                  },
                  '& .MuiTableRow-root:hover': { bgcolor: '#292a43' },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 56 }}>Enabled</TableCell>
                    <TableCell>Key</TableCell>
                    <TableCell>Value</TableCell>
                    <TableCell sx={{ width: 56 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {headers.map((header, i) => (
                    <TableRow key={i} hover tabIndex={-1}>
                      <TableCell>
                        <Checkbox
                          checked={header.enabled}
                          onChange={() => toggleHeader(i)}
                          sx={{ color: '#7c4dff' }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          variant="filled"
                          sx={{
                            input: {
                              fontFamily: 'monospace',
                              bgcolor: '#2b2c44',
                              color: '#ddd',
                              px: 1,
                              borderRadius: 1,
                              fontSize: 14,
                            },
                          }}
                          value={header.key}
                          onChange={e => updateHeader(i, 'key', e.target.value)}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          variant="filled"
                          sx={{
                            input: {
                              fontFamily: 'monospace',
                              bgcolor: '#2b2c44',
                              color: '#ddd',
                              px: 1,
                              borderRadius: 1,
                              fontSize: 14,
                            },
                          }}
                          value={header.value}
                          onChange={e => updateHeader(i, 'value', e.target.value)}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton onClick={() => removeHeader(i)} size="small" sx={{ color: '#ff5363' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                fullWidth
                sx={{
                  mt: 2,
                  borderColor: '#7c4dff',
                  color: '#7c4dff',
                  fontWeight: '600',
                  '&:hover': {
                    bgcolor: '#7c4dff22',
                    borderColor: '#7c4dff',
                  },
                }}
                onClick={addHeader}
              >
                Add Header
              </Button>
            </Box>

            {/* Request Body Editor */}
            <Box>
              <Typography
                gutterBottom
                sx={{ color: '#9b9baf', fontWeight: '600', letterSpacing: 0.7, mb: 1 }}
              >
                Request Body
              </Typography>
              {encryptEnabled && (
                <Alert severity="info" sx={{ mb: 1, fontSize: 14, borderRadius: 2 }}>
                  Request body will be encrypted before sending
                </Alert>
              )}
              <TextField
                multiline
                // minRows={8}
                maxRows={12}
                fullWidth
                value={body}
                onChange={e => setBody(e.target.value)}
                placeholder="Request body (JSON)"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: 14,
                  bgcolor: '#1f2035',
                  color: '#ddd',
                  '& textarea': { fontFamily: 'monospace', fontSize: 14, color: '#ddd' },
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#44456b',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#7c4dff',
                  },
                }}
                disabled={loading}
              />
            </Box>
          </Paper>

          {/* Response Panel */}
          <Paper
            elevation={2}
            sx={{
              p: 3,
              borderRadius: 3,
              bgcolor: '#191b2a',
              boxShadow: '0 0 20px rgba(124, 77, 255, 0.2)',
              color: '#c0c0ff',
              // minHeight: 200,
              userSelect: 'text',
            }}
          >
            <Typography  gutterBottom sx={{ mb: 2, color: '#a0a0d8', fontWeight: 600 }}>
              Response
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2, fontWeight: '600' }}>
                {error}
              </Alert>
            )}

            {loading ? (
              <Box
                sx={{
                  // height: 180,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                  // gap: 1,
                }}
              >
                <CircularProgress size={36} sx={{ color: '#7c4dff' }} />
                <Typography sx={{ color: '#7c4dff', fontWeight: '600' }}>Sending request...</Typography>
              </Box>
            ) : response ? (
              <Box
                sx={{
                  animation: 'fadeInUp 0.4s ease forwards',
                  '@keyframes fadeInUp': {
                    '0%': { opacity: 0, transform: 'translateY(10px)' },
                    '100%': { opacity: 1, transform: 'translateY(0)' },
                  },
                }}
              >
                <Typography sx={{ mb: 1, fontWeight: 600 }}>
                  Status: {response.status} {response.encrypted && '(Encrypted)'}
                </Typography>

                <Box
                  component="pre"
                  sx={{
                    p: 2,
                    bgcolor: '#121224',
                    borderRadius: 3,
                    maxHeight: 280,
                    overflowY: 'auto',
                    fontFamily: 'monospace',
                    fontSize: 14,
                    color: '#ddd',
                    userSelect: 'text',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {typeof response.data === 'object'
                    ? JSON.stringify(response.data, null, 2)
                    : response.data}
                </Box>

                <Box >
                  <CryptoTool
                    defaultKey={SECRET_KEY}
                    value={response.source}
                    readOnly
                    Encrypt={false}
                  />
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#6c6c9a',
                }}
              >
                <Typography>Send a request to see the response here</Typography>
              </Box>
            )}
          </Paper>
        </Box>

      )}

      {/* Snackbar for copy notification */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          cURL command copied to clipboard!
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Main App Component (not changed)
// const PostmanRealAPi = ({ endpoint, setMyOrgName, myOrgName }) => {
//   return (
//     <div style={{ padding: '20px' }}>
//       <ApiTester endpoint={endpoint} setMyOrgName={setMyOrgName} myOrgName={myOrgName} />
//     </div>
//   );
// };

export default PostmanRealAPi;
